import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete, {
  createFilterOptions
} from '@material-ui/lab/Autocomplete'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'

import {
  createPreschoolBatch,
  fetchPreschoolBatchOptions,
  searchTeachers
} from 'api'
import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  field: { marginBottom: theme.spacing(2), width: '100%' },
  row: { display: 'flex', gap: theme.spacing(2) },
  infoBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  summaryBox: {
    backgroundColor: '#e8f4fd',
    border: '1px solid #90caf9',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  warnBox: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe082',
    borderRadius: '4px',
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(2)
  },
  // Deliberately louder than warnBox: a shared room is a choice, a missing
  // program fee is a batch nobody can book into.
  feeWarnBox: {
    backgroundColor: '#fdecea',
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  note: { fontSize: '13px', color: theme.palette.text.secondary },
  heading: { fontSize: '14px', fontWeight: 600, marginBottom: theme.spacing(1) }
}))

// Powers the "type a name that isn't in the list" half of the Room dropdown.
const roomFilter = createFilterOptions()

const EMPTY = {
  centre: null,
  program: null,
  standard: '',
  // Either an existing room object from the options endpoint, or
  // { isNew: true, name } for one to be created. Null until chosen.
  room: null,
  teacher: null,
  capacity: '',
  startTime: '',
  endTime: '',
  startDate: '',
  endDate: ''
}

/**
 * Super-Admin-only creation of ONE preschool batch.
 *
 * Replaces a two-command runbook (create_school_and_care_batches, then
 * create_school_and_care_data) for the single-batch case. The backend derives
 * the Course from programme + grade and writes the batch, a year of schedules
 * and the capacity row in one transaction, so there is no half-created state to
 * clean up and no ordering for the operator to get right.
 *
 * Bulk CSV creation is deliberately NOT here — that stays on the commands until
 * it gets its own screen.
 */
const CreatePreschoolBatchForm = ({ isModalOpen, closeModal, onCreated }) => {
  const classes = useStyles()
  const [options, setOptions] = React.useState(null)
  const [teachers, setTeachers] = React.useState([])
  const [teacherLoading, setTeacherLoading] = React.useState(false)
  const teacherTimer = React.useRef(null)
  const [rooms, setRooms] = React.useState([])
  // (programme, grade) pairs at the selected centre that have an active
  // program fee, with the dates its instalments cover.
  const [feeCoverage, setFeeCoverage] = React.useState([])
  const [form, setForm] = React.useState(EMPTY)
  const [isSaving, setIsSaving] = React.useState(false)

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }))

  React.useEffect(() => {
    if (!isModalOpen) {
      setForm(EMPTY)
      setRooms([])
      setFeeCoverage([])
      return
    }
    ;(async () => {
      try {
        const optionsResponse = await fetchPreschoolBatchOptions()
        setOptions(optionsResponse?.data || null)
      } catch (error) {
        eventAlert(
          error?.message || 'Unable to load the batch creation form.',
          constants.ERROR
        )
        closeModal()
      }
    })()
  }, [isModalOpen])

  // Teachers are searched server side, never preloaded. There are ~1127 of
  // them and a single page reaches 500, so a preloaded list silently hides the
  // rest — and because the client then sorts what it got, the list looks
  // complete. TeacherAPI matches on first name, last name and phone number.
  const runTeacherSearch = React.useCallback((text) => {
    if (teacherTimer.current) clearTimeout(teacherTimer.current)
    teacherTimer.current = setTimeout(async () => {
      setTeacherLoading(true)
      try {
        setTeachers(await searchTeachers({ search: text }))
      } catch (error) {
        setTeachers([])
      } finally {
        setTeacherLoading(false)
      }
    }, 300)
  }, [])

  React.useEffect(() => {
    if (!isModalOpen) return undefined
    runTeacherSearch('')
    return () => teacherTimer.current && clearTimeout(teacherTimer.current)
  }, [isModalOpen, runTeacherSearch])

  // Rooms are per-centre, and the batch_count they carry is the whole point of
  // showing them: the runbook asks for a room that nothing else is using.
  React.useEffect(() => {
    if (!form.centre?.uuid) {
      setRooms([])
      setFeeCoverage([])
      return
    }
    ;(async () => {
      try {
        const response = await fetchPreschoolBatchOptions({
          centre: form.centre.uuid
        })
        setRooms(response?.data?.rooms || [])
        setFeeCoverage(response?.data?.program_fees || [])
      } catch (error) {
        setRooms([])
        setFeeCoverage([])
      }
    })()
  }, [form.centre])

  // Picking a programme fixes both the grades on offer (only those with a
  // Course) and the hours it is normally run at.
  const onProgramChange = (program) => {
    const academicYear = (options?.academic_years || []).find((y) =>
      dayjs().isBefore(dayjs(y.end_date))
    )
    set({
      program,
      standard: '',
      startTime: program?.start_time
        ? program.start_time.replace('-', ':')
        : '',
      endTime: program?.end_time ? program.end_time.replace('-', ':') : '',
      startDate: form.startDate || dayjs().format('YYYY-MM-DD'),
      endDate: form.endDate || academicYear?.end_date || ''
    })
  }

  const grades = form.program?.standards || []

  const roomHelperText = !form.centre
    ? 'Pick a centre first.'
    : rooms.length
    ? 'Select an existing room, or type a new name to create one.'
    : 'No rooms at this centre yet — type a name to create one.'

  // A batch with no program fee behind it is created fine and then cannot be
  // booked: the enrolment modal resolves the fee by centre + programme + grade
  // + start date, and posts the cart with no program_fee when it finds none,
  // which checkout rejects with "Program fee doesnt exist!". Fees are created
  // per centre per academic year by a separate command, so a centre can have
  // batches and no fees at all. Warn here rather than let a parent hit it.
  const feeWarning = React.useMemo(() => {
    if (!form.centre || !form.program || !form.standard) return null
    // Resolved here rather than from `selectedGrade` below, which is declared
    // after this memo and would be in the temporal dead zone at render.
    const grade = (form.program.standards || []).find(
      (g) => g.uuid === form.standard
    )
    const gradeCode = grade?.short_code || 'that grade'
    const match = feeCoverage.find(
      (fee) =>
        fee.program === form.program.uuid && fee.standard === form.standard
    )
    if (!match) {
      return `No active program fee at ${form.centre.name} for ${form.program.name} (${gradeCode}). This batch can be created, but nobody will be able to book into it until the fee is set up.`
    }
    if (
      form.startDate &&
      match.covers_from &&
      match.covers_to &&
      (form.startDate < match.covers_from || form.startDate > match.covers_to)
    ) {
      return `The program fee for ${form.program.name} (${gradeCode}) at ${form.centre.name} only covers ${match.covers_from} to ${match.covers_to}. A batch starting ${form.startDate} will not be bookable.`
    }
    return null
  }, [feeCoverage, form.centre, form.program, form.standard, form.startDate])

  const isComplete =
    form.centre &&
    form.program &&
    form.standard &&
    form.teacher &&
    form.capacity &&
    form.startTime &&
    form.endTime &&
    form.startDate &&
    form.endDate &&
    form.room

  const onSave = async () => {
    if (!isComplete) {
      eventAlert(
        'Fill in every field before creating the batch.',
        constants.ERROR
      )
      return
    }
    setIsSaving(true)
    try {
      const response = await createPreschoolBatch({
        centre: form.centre.uuid,
        program: form.program.uuid,
        standard: form.standard,
        teacher: form.teacher.uuid,
        capacity: Number(form.capacity),
        start_date: form.startDate,
        end_date: form.endDate,
        start_time: form.startTime,
        end_time: form.endTime,
        // The backend takes one or the other, never both: `room` shares an
        // existing room, `room_name` mints a new one.
        ...(form.room.isNew
          ? { room_name: form.room.name }
          : { room: form.room.uuid })
      })
      const created = response?.data?.batch
      eventAlert(
        `Batch created for ${created?.program} at ${created?.centre} in ` +
          `${created?.room} — ${response?.data?.schedule_count} sessions scheduled, ` +
          `capacity ${created?.enrolment_capacity}.`,
        constants.SUCCESS
      )
      closeModal()
      if (onCreated) onCreated()
    } catch (error) {
      eventAlert(
        error?.message || 'Unable to create the batch.',
        constants.ERROR
      )
    } finally {
      setIsSaving(false)
    }
  }

  const selectedGrade = grades.find((g) => g.uuid === form.standard)

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle="Create Preschool Batch"
      buttonLabel={isSaving ? 'Creating…' : 'Create batch'}
      loading={isSaving}
      onSaveClick={onSave}
      disableBackdropClick
      maxWidth="sm"
    >
      <Box className={classes.infoBox}>
        <Typography className={classes.note}>
          Creates one preschool batch running <strong>Monday to Friday</strong>,
          its schedules for the whole period, and its enrolment capacity — in a
          single step. The course is derived from the programme and grade, so
          only grades that already have a course are listed.
        </Typography>
      </Box>

      <Autocomplete
        className={classes.field}
        options={options?.centres || []}
        value={form.centre}
        getOptionLabel={(o) => o?.name || ''}
        getOptionSelected={(a, b) => a?.uuid === b?.uuid}
        onChange={(_, value) => set({ centre: value, room: null })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Centre"
            variant="outlined"
            size="small"
          />
        )}
      />

      <Autocomplete
        className={classes.field}
        options={options?.programs || []}
        value={form.program}
        getOptionLabel={(o) => o?.name || ''}
        getOptionSelected={(a, b) => a?.uuid === b?.uuid}
        onChange={(_, value) => onProgramChange(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Programme"
            variant="outlined"
            size="small"
          />
        )}
      />

      <FormControl variant="outlined" size="small" className={classes.field}>
        {/* labelId/id wire the label to the control. Without them MUI v4's
            Select renders a div with no association, so the label is decoration
            only — screen readers announce nothing. */}
        <InputLabel id="preschool-grade-label">Grade</InputLabel>
        <Select
          labelId="preschool-grade-label"
          id="preschool-grade"
          value={form.standard}
          label="Grade"
          disabled={!form.program}
          onChange={(e) => set({ standard: e.target.value })}
        >
          {grades.map((grade) => (
            <MenuItem key={grade.uuid} value={grade.uuid}>
              {grade.name} ({grade.short_code})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Autocomplete
        className={classes.field}
        options={rooms}
        value={form.room}
        disabled={!form.centre}
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option?.name || ''
        }
        getOptionSelected={(option, value) =>
          option.uuid
            ? option.uuid === value?.uuid
            : option.name === value?.name
        }
        renderOption={(option) =>
          option.isNew
            ? `Create new room "${option.name}"`
            : `${option.name} — ${option.batch_count} batch(es) using it`
        }
        filterOptions={(options, params) => {
          const filtered = roomFilter(options, params)
          const typed = params.inputValue.trim()
          // Only offer "create" for a name that is genuinely free. Offering it
          // for a name that already exists would let ops create what looks like
          // a fresh room but is really a request the backend rejects — and if
          // it did not reject it, the two batches would silently share capacity.
          const alreadyExists = options.some(
            (option) => option.name.toLowerCase() === typed.toLowerCase()
          )
          if (typed && !alreadyExists) {
            filtered.push({ isNew: true, name: typed })
          }
          return filtered
        }}
        onChange={(_, value) => {
          if (typeof value !== 'string') {
            set({ room: value })
            return
          }
          // Enter pressed on raw text rather than picking an option. Resolve it
          // to the existing room if the name matches one, so typing the name of
          // a room that exists never silently becomes a create-new request.
          const typed = value.trim()
          const match = rooms.find(
            (room) => room.name.toLowerCase() === typed.toLowerCase()
          )
          set({ room: match || (typed ? { isNew: true, name: typed } : null) })
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Room"
            variant="outlined"
            size="small"
            placeholder={form.centre ? 'Select or type a room name' : ''}
            helperText={roomHelperText}
          />
        )}
      />
      {form.room?.isNew ? (
        <Box className={classes.warnBox}>
          <Typography className={classes.note}>
            <strong>{form.room.name}</strong> does not exist at{' '}
            {form.centre?.name} and will be created. Its capacity belongs to
            this batch alone.
          </Typography>
        </Box>
      ) : null}
      {form.room && !form.room.isNew ? (
        <Box className={classes.warnBox}>
          <Typography className={classes.note}>
            Sharing <strong>{form.room.name}</strong>, already used by{' '}
            {form.room.batch_count} batch(es). Each batch keeps its own seat
            count — sharing a room does <strong>not</strong> pool capacity
            between them.
          </Typography>
        </Box>
      ) : null}

      <Autocomplete
        className={classes.field}
        options={teachers}
        value={form.teacher}
        loading={teacherLoading}
        // The options ARE the search result; filtering them again in the
        // browser would re-hide anything the server matched on a field the
        // label does not show.
        filterOptions={(x) => x}
        getOptionLabel={(o) => (o ? `${o.full_name} (${o.phone_number})` : '')}
        getOptionSelected={(a, b) => a?.uuid === b?.uuid}
        onChange={(_, value) => set({ teacher: value })}
        onInputChange={(_, text, reason) => {
          if (reason === 'input') runTeacherSearch(text)
        }}
        noOptionsText={
          teacherLoading ? 'Searching…' : 'No teacher matches that'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Teacher"
            variant="outlined"
            size="small"
            placeholder="Search by name or phone number"
            helperText="Searches every teacher, not just the first page."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {teacherLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />

      <Box className={classes.row}>
        <TextField
          className={classes.field}
          id="preschool-start-time"
          label="Start time"
          type="time"
          variant="outlined"
          size="small"
          value={form.startTime}
          onChange={(e) => set({ startTime: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          className={classes.field}
          id="preschool-end-time"
          label="End time"
          type="time"
          variant="outlined"
          size="small"
          value={form.endTime}
          onChange={(e) => set({ endTime: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Box className={classes.row}>
        <TextField
          className={classes.field}
          id="preschool-start-date"
          label="Batch start date"
          type="date"
          variant="outlined"
          size="small"
          value={form.startDate}
          onChange={(e) => set({ startDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          className={classes.field}
          id="preschool-end-date"
          label="Batch end date"
          type="date"
          variant="outlined"
          size="small"
          value={form.endDate}
          onChange={(e) => set({ endDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <TextField
        className={classes.field}
        id="preschool-capacity"
        label="Capacity"
        type="number"
        variant="outlined"
        size="small"
        value={form.capacity}
        onChange={(e) => set({ capacity: e.target.value })}
        helperText="Seats for this programme in this room."
      />

      {feeWarning ? (
        <Box className={classes.feeWarnBox}>
          <Typography className={classes.heading}>
            No program fee — this batch will not be bookable
          </Typography>
          <Typography className={classes.note}>{feeWarning}</Typography>
        </Box>
      ) : null}

      {isComplete ? (
        <Box className={classes.summaryBox}>
          <Typography className={classes.heading}>Will create</Typography>
          <Typography className={classes.note}>
            {form.program.name} · {selectedGrade?.short_code} ·{' '}
            {form.centre.name} · {form.room.name}
            {form.room.isNew ? ' (new room)' : ' (shared room)'}
            <br />
            Mon–Fri {form.startTime}–{form.endTime}, {form.startDate} to{' '}
            {form.endDate}
            <br />
            Capacity {form.capacity}, teacher {form.teacher.full_name}
          </Typography>
        </Box>
      ) : null}
    </ModalForm>
  )
}

CreatePreschoolBatchForm.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onCreated: PropTypes.func
}

export default CreatePreschoolBatchForm
